import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Truck, ShieldCheck, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SafeImage from '@/components/SafeImage';

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      {/* Background image + layered gradients for stronger contrast */}
      <div className="absolute inset-0">
        <SafeImage
          src="https://ymrzpmvwuxphomnuzgvp.supabase.co/storage/v1/object/public/gallery/aabef02f-4871-4e7b-bf38-2c0a3985d112/w800.jpg"
          srcSet="https://ymrzpmvwuxphomnuzgvp.supabase.co/storage/v1/object/public/gallery/aabef02f-4871-4e7b-bf38-2c0a3985d112/w400.jpg 400w, https://ymrzpmvwuxphomnuzgvp.supabase.co/storage/v1/object/public/gallery/aabef02f-4871-4e7b-bf38-2c0a3985d112/w800.jpg 800w, https://ymrzpmvwuxphomnuzgvp.supabase.co/storage/v1/object/public/gallery/aabef02f-4871-4e7b-bf38-2c0a3985d112/w1600.jpg 1600w"
          sizes="100vw"
          alt=""
          width={1600}
          height={900}
          fetchPriority="high"
          decoding="async"
          loading="eager"
          eager
          wrapperClassName="h-full w-full"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/25" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-gold/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -top-20 right-0 h-64 w-64 rounded-full bg-primary/30 blur-3xl" aria-hidden="true" />
      </div>

      <div className="container relative py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-2xl animate-fade-up text-center sm:text-left mx-auto sm:mx-0">
          {/* Eyebrow */}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/25 px-3 py-1.5 text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase text-white">
            <Sparkles className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
            Handgefertigte Kunstwerke
          </span>

          {/* Headline — clear hierarchy: H1 dominates */}
          <h1 className="mt-5 sm:mt-6 heading-display text-[2rem] xs:text-[2.25rem] leading-[1.15] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight text-balance">
            Handgefertigte Kunst
            <span className="block mt-1.5 sm:mt-2 text-gold italic font-normal">
              &amp; personalisierte Gravuren
            </span>
          </h1>

          {/* Subheadline — supporting, smaller */}
          <p className="mt-5 sm:mt-5 text-base sm:text-lg md:text-xl text-white leading-relaxed max-w-xl mx-auto sm:mx-0">
            Einzigartige Blattgravuren, individueller Modeschmuck und besondere Geschenke – mit Liebe gefertigt von <span className="text-gold font-semibold">cycy</span> in Düsseldorf.
          </p>

          {/* Primary + secondary CTA — clear visual weight difference */}
          <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button
              asChild
              size="lg"
              className="font-body h-12 sm:h-12 px-6 text-base font-semibold shadow-[var(--shadow-elegant)] group w-full sm:w-auto"
            >
              <Link to="/shop">
                Kollektion entdecken
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
            <Link
              to="/ueber-uns"
              className="inline-flex items-center justify-center gap-1.5 h-11 sm:h-12 px-2 text-base font-medium text-white hover:text-gold transition-colors group"
            >
              <span className="border-b border-white/50 group-hover:border-gold pb-0.5">
                Mehr über cycy
              </span>
              <ArrowRight className="h-4 w-4 opacity-80 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </div>

          {/* Trust strip — divider + uniform icon weight */}
          <div className="mt-7 sm:mt-8 pt-5 sm:pt-5 border-t border-white/20 flex flex-wrap justify-center sm:justify-start items-center gap-x-5 gap-y-2.5 text-sm text-white">
            <span className="inline-flex items-center gap-1.5">
              <Truck className="h-4 w-4 text-gold" aria-hidden="true" /> Versand ab 59 € gratis
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-gold" aria-hidden="true" /> Sichere Zahlung
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-gold text-gold" aria-hidden="true" /> 100+ Kunden
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
